import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Award, Calendar, ExternalLink, Euro, Layers } from 'lucide-react';
import { supabase } from '../lib/supabase';
import InfluencerAvatar from '../components/InfluencerAvatar';

interface SocialProfile {
  platform: 'instagram' | 'tiktok' | 'snapchat' | 'youtube';
  username: string;
  followers: number | null;
  url: string | null;
}

function PlatformIcon({ platform }: { platform: SocialProfile['platform'] }) {
  const cls = "w-4 h-4 flex-shrink-0";
  if (platform === 'instagram') return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
      <circle cx="12" cy="12" r="4"/>
      <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
    </svg>
  );
  if (platform === 'tiktok') return (
    <svg className={cls} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.82a8.16 8.16 0 004.77 1.52V6.9a4.85 4.85 0 01-1-.21z"/>
    </svg>
  );
  if (platform === 'snapchat') return (
    <svg className={cls} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.166 2C9.623 2 7.502 3.657 7.502 6.42v.63c-.36-.07-.71-.11-1.02-.11-.58 0-.98.14-.98.14s.09.87.66 1.4c-.23.12-.5.2-.8.2-.22 0-.44-.04-.64-.12 0 0 .23 1.14 1.56 1.56-.29.3-.76.5-1.45.5 0 0 .58 1.19 2.72 1.38-.18.25-.57.47-1.27.6 0 0 1.16 1.18 3.74 1.18.07.35.11.72.11 1.09 0 .88-.19 1.76-.56 2.56-.05.11.18.15.41.15.82 0 2.17-.44 2.17-1.28 0-.07 0-.14-.01-.21.25.03.5.04.75.04s.5-.01.75-.04c-.01.07-.01.14-.01.21 0 .84 1.35 1.28 2.17 1.28.23 0 .46-.04.41-.15-.37-.8-.56-1.68-.56-2.56 0-.37.04-.74.11-1.09 2.58 0 3.74-1.18 3.74-1.18-.7-.13-1.09-.35-1.27-.6 2.14-.19 2.72-1.38 2.72-1.38-.69 0-1.16-.2-1.45-.5 1.33-.42 1.56-1.56 1.56-1.56-.2.08-.42.12-.64.12-.3 0-.57-.08-.8-.2.57-.53.66-1.4.66-1.4s-.4-.14-.98-.14c-.31 0-.62.04-1.02.11v-.63C16.666 3.657 14.709 2 12.166 2z"/>
    </svg>
  );
  // youtube
  return (
    <svg className={cls} viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  );
}

interface InfluencerData {
  id: string;
  name: string | null;
  display_name: string | null;
  username: string;
  platform: string;
  followers_count: number;
  engagement_rate: number;
  category: string | null;
  profile_image_url: string | null;
  profile_url: string | null;
  verified: boolean;
}

interface Collab {
  id: string;
  name: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
  budget: number;
  description: string | null;
  platform: string;
  client_name?: string;
  signed_date?: string | null;
  influencers?: any[];
  invoice_sent?: boolean;
  payment_received?: boolean;
  quote_sent?: boolean;
  contract_signed?: boolean;
}

export default function PublicInfluencerView() {
  const { influencerId } = useParams<{ influencerId: string }>();
  const navigate = useNavigate();
  const [influencer, setInfluencer] = useState<InfluencerData | null>(null);
  const [collaborations, setCollaborations] = useState<Collab[]>([]);
  const [socialProfiles, setSocialProfiles] = useState<SocialProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agencyName, setAgencyName] = useState<string>('');

  useEffect(() => {
    loadInfluencerData();
  }, [influencerId]);

  const loadInfluencerData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!influencerId) {
        setError('Lien invalide');
        return;
      }

      const { data: influencerData, error: influencerError } = await supabase
        .from('influencers')
        .select('*')
        .eq('id', influencerId)
        .maybeSingle();

      if (influencerError) {
        setError(`Erreur lors du chargement: ${influencerError.message}`);
        return;
      }

      if (!influencerData) {
        setError('Influenceur non trouvé');
        return;
      }

      setInfluencer(influencerData);

      const { data: orgInfluencers } = await supabase
        .from('organization_influencers')
        .select('organization_id')
        .eq('influencer_id', influencerId)
        .limit(1);

      let agencyId: string | null = null;

      if (orgInfluencers && orgInfluencers.length > 0) {
        const userId = orgInfluencers[0].organization_id;

        const { data: userData } = await supabase
          .from('users')
          .select('agency_id')
          .eq('id', userId)
          .maybeSingle();

        if (userData?.agency_id) {
          agencyId = userData.agency_id;
          const { data: agencyData } = await supabase
            .from('agencies')
            .select('name')
            .eq('id', agencyId)
            .maybeSingle();

          if (agencyData) setAgencyName(agencyData.name);
        }
      }

      const { data: collabsData } = await supabase.from('collabs').select('*');

      const filteredCollabs = (collabsData || []).filter(collab => {
        if (!collab.influencers || !Array.isArray(collab.influencers)) return false;
        const isInfluencerInCollab = collab.influencers.some((inf: any) => inf.id === influencerId);
        const isAgencyMatch = !agencyId || collab.agency_id === agencyId;
        return isInfluencerInCollab && isAgencyMatch;
      });
      setCollaborations(filteredCollabs);

      // Load other social networks in parallel
      const [tiktokRes, snapRes, ytRes] = await Promise.all([
        supabase.from('tiktok_profiles').select('tiktok_username, followers_count').eq('influencer_id', influencerId).maybeSingle(),
        supabase.from('snapchat_profiles').select('username, subscriber_count').eq('influencer_id', influencerId).maybeSingle(),
        supabase.from('youtube_profiles').select('youtube_handle, subscribers_count, channel_id').eq('influencer_id', influencerId).maybeSingle(),
      ]);

      const profiles: SocialProfile[] = [];

      // Instagram is the main platform
      profiles.push({
        platform: 'instagram',
        username: influencerData.username,
        followers: influencerData.followers_count,
        url: influencerData.profile_url,
      });

      if (tiktokRes.data?.tiktok_username) {
        profiles.push({
          platform: 'tiktok',
          username: tiktokRes.data.tiktok_username,
          followers: tiktokRes.data.followers_count ?? null,
          url: `https://www.tiktok.com/@${tiktokRes.data.tiktok_username}`,
        });
      }

      if (snapRes.data?.username) {
        profiles.push({
          platform: 'snapchat',
          username: snapRes.data.username,
          followers: snapRes.data.subscriber_count ?? null,
          url: `https://www.snapchat.com/add/${snapRes.data.username}`,
        });
      }

      if (ytRes.data?.youtube_handle || ytRes.data?.channel_id) {
        const handle = ytRes.data.youtube_handle || ytRes.data.channel_id;
        profiles.push({
          platform: 'youtube',
          username: handle,
          followers: ytRes.data.subscribers_count ?? null,
          url: `https://www.youtube.com/@${handle}`,
        });
      }

      setSocialProfiles(profiles);
    } catch {
      setError('Erreur lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  };

  const getDisplayName = (inf: InfluencerData) => inf.display_name || inf.name || inf.username;

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatDate = (date: string | null) => {
    if (!date) return 'Date non définie';
    return new Date(date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getStatusBadge = (collab: Collab) => {
    if (collab.invoice_sent || collab.payment_received) {
      return { label: 'Terminée', className: 'bg-emerald-950/60 text-emerald-400 border border-emerald-800/60' };
    }
    if (collab.quote_sent || collab.contract_signed) {
      return { label: 'En cours', className: 'bg-amber-950/60 text-amber-400 border border-amber-800/60' };
    }
    return { label: 'En attente', className: 'bg-slate-800/60 text-slate-400 border border-slate-700/60' };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#888888] text-sm tracking-wide">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !influencer) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center bg-[#1A1A1A] border border-[#2A2A2A] p-8 rounded-2xl max-w-md">
          <div className="w-12 h-12 rounded-full bg-[#2A2A2A] flex items-center justify-center mx-auto mb-4">
            <span className="text-[#888888] text-xl">?</span>
          </div>
          <h2 className="text-xl font-semibold text-[#FAFAFA] mb-2">Page introuvable</h2>
          <p className="text-[#888888] text-sm">{error || 'Une erreur est survenue'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-10 px-4 sm:px-6">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        .font-serif-display { font-family: 'Inter', system-ui, sans-serif; }
        .font-inter { font-family: 'Inter', system-ui, sans-serif; }
        .card-hover { transition: transform 200ms ease, border-color 200ms ease, box-shadow 200ms ease; }
        .card-hover:hover { transform: translateY(-2px); border-color: rgba(212,175,55,0.3) !important; box-shadow: 0 8px 32px rgba(0,0,0,0.4); }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .fade-up { animation: fadeUp 0.5s ease forwards; }
        .fade-up-1 { animation-delay: 0.05s; opacity: 0; }
        .fade-up-2 { animation-delay: 0.15s; opacity: 0; }
        .fade-up-3 { animation-delay: 0.25s; opacity: 0; }
      `}</style>

      <div className="max-w-2xl mx-auto space-y-5 font-inter">

        {/* Header card */}
        <div className="fade-up fade-up-1 bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 mb-7">
            <div className="flex items-center gap-4">
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 rounded-full bg-[#D4AF37]/20 blur-md scale-110" />
                <InfluencerAvatar
                  profileImageUrl={influencer.profile_image_url}
                  name={getDisplayName(influencer)}
                  size="xl"
                />
              </div>
              <div>
                <h1 className="font-serif-display text-2xl sm:text-3xl font-semibold text-[#FAFAFA] flex items-center gap-2">
                  {getDisplayName(influencer)}
                  {influencer.verified && <Award className="w-5 h-5 text-[#D4AF37]" />}
                </h1>
                <p className="text-[#888888] text-sm mt-0.5">@{influencer.username}</p>
                {agencyName && (
                  <p className="text-[#D4AF37]/80 text-xs font-medium mt-1.5 tracking-wide uppercase">
                    {agencyName}
                  </p>
                )}
              </div>
            </div>
            {influencer.profile_url && (
              <a
                href={influencer.profile_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 bg-[#FAFAFA] text-[#0A0A0A] rounded-xl text-sm font-medium hover:bg-[#D4AF37] transition-colors duration-200 whitespace-nowrap flex-shrink-0"
              >
                <ExternalLink className="w-4 h-4" />
                Voir le profil
              </a>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { value: formatNumber(influencer.followers_count), label: 'Abonnés' },
              { value: influencer.engagement_rate ? `${influencer.engagement_rate.toFixed(1)}%` : '—', label: 'Engagement' },
              { value: collaborations.length.toString(), label: 'Collabs' },
            ].map(({ value, label }) => (
              <div key={label} className="bg-[#111111] border border-[#2A2A2A] rounded-xl p-4 text-center">
                <div className="text-lg sm:text-xl font-semibold text-[#FAFAFA]">{value}</div>
                <div className="text-xs text-[#888888] mt-0.5">{label}</div>
              </div>
            ))}
          </div>

          {/* Other social networks */}
          {socialProfiles.length > 1 && (
            <div className="mt-4 pt-4 border-t border-[#2A2A2A]">
              <p className="text-xs text-[#888888] uppercase tracking-widest mb-3">Retrouvez-nous sur</p>
              <div className="flex flex-wrap gap-2">
                {socialProfiles.map((p) => (
                  p.url ? (
                    <a
                      key={p.platform}
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 bg-[#111111] border border-[#2A2A2A] rounded-xl hover:border-[#D4AF37]/40 transition-colors duration-200 group"
                    >
                      <PlatformIcon platform={p.platform} />
                      <div className="text-left">
                        <div className="text-xs font-medium text-[#FAFAFA] group-hover:text-[#D4AF37] transition-colors capitalize">{p.platform}</div>
                        {p.followers && (
                          <div className="text-[10px] text-[#888888]">{formatNumber(p.followers)}</div>
                        )}
                      </div>
                    </a>
                  ) : null
                ))}
              </div>
            </div>
          )}

          {influencer.category && (
            <div className="mt-4">
              <span className="inline-block px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20 rounded-full text-xs font-medium tracking-wide">
                {influencer.category}
              </span>
            </div>
          )}
        </div>

        {/* Collaborations */}
        <div className="fade-up fade-up-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl p-6 sm:p-8">
          <h2 className="font-serif-display text-xl sm:text-2xl font-semibold text-[#FAFAFA] mb-6 flex items-center gap-3">
            <span className="w-8 h-8 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
              <Euro className="w-4 h-4 text-[#D4AF37]" />
            </span>
            Mes Collaborations
          </h2>

          {collaborations.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 rounded-full bg-[#2A2A2A] flex items-center justify-center mx-auto mb-4">
                <Layers className="w-5 h-5 text-[#888888]" />
              </div>
              <p className="text-[#888888] text-sm">Aucune collaboration pour le moment</p>
            </div>
          ) : (
            <div className="space-y-3">
              {collaborations.map((collab) => {
                const statusBadge = getStatusBadge(collab);
                const influencerData = collab.influencers?.find((inf: any) => inf.id === influencerId);
                const influencerRate = influencerData?.rate ? parseFloat(influencerData.rate) : 0;

                return (
                  <div
                    key={collab.id}
                    onClick={() => navigate(`/collaboration/${collab.id}?influencerId=${influencerId}`)}
                    className="bg-[#111111] border border-[#2A2A2A] rounded-xl p-4 sm:p-5 cursor-pointer card-hover"
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <h3 className="font-serif-display text-base sm:text-lg font-semibold italic text-[#FAFAFA] leading-tight">
                        {collab.name}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${statusBadge.className}`}>
                        {statusBadge.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex items-center gap-1.5 text-[#888888] text-xs">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>{formatDate(collab.signed_date || null)}</span>
                      </div>
                      {influencerRate > 0 && (
                        <span className="px-2.5 py-1 bg-emerald-950/50 text-emerald-400 border border-emerald-800/50 rounded-full text-xs font-medium">
                          {influencerRate.toLocaleString('fr-FR')} €
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="fade-up fade-up-3 text-center pb-6">
          <p className="text-[#888888]/50 text-xs tracking-widest uppercase">Propulsé par Campaly</p>
        </div>
      </div>
    </div>
  );
}
